import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Edit3, Send, Download, Plus, Search, Copy, Trash2, Receipt } from "lucide-react";
import { toast } from "sonner";
import api, { API, getToken } from "@/lib/api";

const STATUS_STYLES = {
  Draft: "text-gray-300 bg-gray-400/10 border-gray-400/30",
  Sent: "text-blue-300 bg-blue-400/10 border-blue-400/30",
  Viewed: "text-purple-300 bg-purple-400/10 border-purple-400/30",
  Paid: "text-[#C9963F] bg-[#C9963F]/10 border-[#C9963F]/40",
  Cancelled: "text-red-300 bg-red-400/10 border-red-400/30",
};

function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.Draft;
  return <span className={`inline-flex items-center px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] border ${cls}`} data-testid={`inv-status-${status}`}>{status}</span>;
}

function fmt(s) {
  if (!s) return "—";
  try { return new Date(s.length === 10 ? s + "T00:00:00" : s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return s; }
}

function money(n) {
  return `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function InvoicesList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  async function load() {
    setLoading(true);
    try { const { data } = await api.get("/invoices"); setRows(data || []); }
    catch { toast.error("Could not load invoices"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function remove(inv) {
    if (!window.confirm(`Delete ${inv.invoice_number}?`)) return;
    try { await api.delete(`/invoices/${inv.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  }

  function copyLink(inv) {
    const url = `${window.location.origin}/i/${inv.public_token}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Client link copied"));
  }

  async function download(inv) {
    try {
      const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `${inv.invoice_number}.pdf`; document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("PDF download failed"); }
  }

  const filtered = rows.filter((r) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (r.client_name || "").toLowerCase().includes(s) || (r.invoice_number || "").toLowerCase().includes(s);
  });

  return (
    <div data-testid="invoices-list-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-2">Billing</div>
          <h1 className="font-serif-brand text-4xl sm:text-5xl tracking-tight">Invoices</h1>
          <p className="text-white/50 mt-2 text-sm">{rows.length} invoice{rows.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" data-testid="inv-search"
              className="bg-[#0a0a0a] border border-white/10 pl-9 pr-3 py-2 text-sm focus:border-[#C9963F] focus:outline-none text-white" />
          </div>
          <button onClick={() => navigate("/admin/invoices/new")} data-testid="inv-new-btn"
            className="inline-flex items-center gap-2 bg-[#C9963F] text-black px-4 py-2 text-sm font-medium hover:bg-[#A67A2D]">
            <Plus size={14} /> New Invoice
          </button>
        </div>
      </div>

      {loading ? <div className="text-white/40 text-sm">Loading…</div> : (
        filtered.length === 0 ? (
          <div className="bg-[#0e0e0e] border border-white/10 p-10 text-center text-white/40 text-sm">
            No invoices yet. Click <span className="text-[#C9963F]">New Invoice</span> to create one.
          </div>
        ) : (
          <div className="bg-[#0e0e0e] border border-white/10 divide-y divide-white/5">
            {filtered.map((r) => (
              <div key={r.id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`inv-row-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[10px] uppercase tracking-wider text-[#C9963F]">{r.invoice_number}</span>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="text-white text-sm font-medium truncate">{r.client_name}</div>
                  <div className="text-[11px] text-white/50">{fmt(r.created_at)}{r.event_date ? ` · Event ${fmt(r.event_date)}` : ""}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-serif-brand text-xl text-white">{money(r.total)}</div>
                  <div className="text-[10px] uppercase tracking-wider text-white/40">
                    {Number(r.amount_outstanding || 0) === 0 ? "Paid in full" : `${money(r.amount_outstanding)} outstanding`}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {r.status !== "Paid" && r.status !== "Cancelled" && (
                    <button onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await api.patch(`/invoices/${r.id}/status`, { status: "Paid" });
                        toast.success("Marked paid");
                        load();
                      } catch { toast.error("Failed"); }
                    }} data-testid={`inv-mark-paid-${r.id}`}
                      className="text-[10px] uppercase tracking-wider text-[#C9963F] border border-[#C9963F]/40 px-2 py-1 hover:bg-[#C9963F]/10">
                      Mark Paid
                    </button>
                  )}
                  <Link to={`/admin/invoices/${r.id}`} data-testid={`inv-edit-${r.id}`} className="p-1.5 text-white/60 hover:text-[#C9963F]" title="Edit"><Edit3 size={14} /></Link>
                  <button onClick={() => copyLink(r)} data-testid={`inv-copy-${r.id}`} className="p-1.5 text-white/60 hover:text-[#C9963F]" title="Copy client link"><Copy size={14} /></button>
                  <button onClick={() => download(r)} data-testid={`inv-pdf-${r.id}`} className="p-1.5 text-white/60 hover:text-[#C9963F]" title="Download PDF"><Download size={14} /></button>
                  <button onClick={() => remove(r)} data-testid={`inv-delete-${r.id}`} className="p-1.5 text-white/60 hover:text-red-400" title="Delete"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
